import { BillingInfo, SimplePickerList, useBillingContext } from '@shipstation/financial-services-ui-components';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { FlexColumn, Gap } from 'ui-components/components/flex';

import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { getBillingInfoFromShipFrom } from '../../../../../../financial-services-ui/mapping';
import { TaxIdentifierSection } from '../../../../../../pages/settings/payment-and-subscription/components/tax-identifier-section';
import Global from '../../../../../../store/global-selectors';
import { AddressDropdown } from '../../../address-dropdown';
import styles from './styles.module.css';
import { useOnComponentMount } from '../../../../../../hooks/use-on-component-mount';

export const AddPaymentMethodBody = ({
  creditCardConfig,
  payByBankConfig,
  locale,
  onPaymentTypeChange,
  currencyCode,
  acceptedPaymentTypes,
  selectedPaymentType,
  showCreditCardPlaceholders,
  creditCardPlaceholders,
  setSubmitFunction,
  onSubmit,
  onError,
}) => {
  const shipFroms = useSelector(Global.selectSellerShipFroms) || emptyArray;
  const initialBillingInfo = shipFroms?.length ? shipFroms.find((shipFrom) => shipFrom.isDefault) : undefined;
  const [isAddressLoaded, setIsAddressLoaded] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(initialBillingInfo || emptyObject);

  const { isBillingInfoValid, billingInfo, isEditing, setBillingInfo } = useBillingContext();

  const performValidationFunctionRef = useRef(undefined);

  const setPerformValidationFunction = useCallback((fn) => {
    performValidationFunctionRef.current = fn;
  }, []);

  const handleAddressChange = useCallback(
    (addressSelection) => {
      setBillingInfo(getBillingInfoFromShipFrom(addressSelection?.originAddress) || emptyObject, true);
      setSelectedAddress(addressSelection);
    },
    [setBillingInfo],
  );

  const handleSubmit = useCallback(() => {
    const errors = [];

    if (!isBillingInfoValid) {
      performValidationFunctionRef.current?.();
      errors.push('Invalid Billing address');
    }

    if (errors.length > 0) {
      onError?.(new Error(errors.join(', ')));
      return;
    }

    onSubmit?.({
      billingInfo,
      usageData: [],
    });
  }, [isBillingInfoValid, onSubmit, billingInfo, onError]);

  useEffect(() => {
    setSubmitFunction?.(handleSubmit);
  }, [handleSubmit, setSubmitFunction]);

  useOnComponentMount(() => {
    if (initialBillingInfo) {
      const result = getBillingInfoFromShipFrom(initialBillingInfo?.originAddress) || emptyObject;
      setBillingInfo(result, true);
    }
    setIsAddressLoaded(true);
  });

  return (
    <FlexColumn gap={Gap.Space32}>
      <div>
        <SimplePickerList
          creditCardConfig={creditCardConfig}
          creditCardPlaceholders={creditCardPlaceholders}
          currencyCode={currencyCode}
          locale={locale}
          payByBankConfig={payByBankConfig}
          paymentTypes={acceptedPaymentTypes}
          selectedType={selectedPaymentType}
          setSelectedType={onPaymentTypeChange}
          showCreditCardPlaceholders={showCreditCardPlaceholders}
        />
      </div>
      <>
        {isEditing && !!shipFroms?.length && (
          <div>
            <AddressDropdown onAddressChange={handleAddressChange} selectedAddress={selectedAddress} shipFroms={shipFroms} />
          </div>
        )}
        {isAddressLoaded && (
          <div>
            <BillingInfo setPerformFormValidation={setPerformValidationFunction} />
          </div>
        )}
        <TaxIdentifierSection formClassName={styles['tax-identifier-form']} />
      </>
    </FlexColumn>
  );
};
